import React, { useRef } from 'react';
import { UploadCloud, FileCheck, AlertCircle, X, Plus, Trash2 } from 'lucide-react';
import { FormField } from '../../../components';
import {
    MAX_FILES_PER_DOCUMENT,
    toDocumentList,
    documentLabel,
} from '../../../utils/residenceDocuments';

// Back / Save live in the ConvertClient page header, top-right.
const Documentations = ({ updateData, data }) => {
    const docs = data.documentations || {};

    // Every slot holds a list, so a document type can carry several files
    // (both sides of a licence, a multi-page contract...). Newly picked files
    // are appended; the list is capped so one slot can't grow unbounded.
    const addFiles = (field, e) => {
        const picked = Array.from(e.target.files || []);
        if (picked.length === 0) return;
        const current = toDocumentList(docs[field]);
        updateData('documentations', {
            [field]: [...current, ...picked].slice(0, MAX_FILES_PER_DOCUMENT),
        });
        // Let the same file be picked again after a remove.
        e.target.value = '';
    };

    const removeFileAt = (field, index) => {
        const next = toDocumentList(docs[field]).filter((_, i) => i !== index);
        updateData('documentations', { [field]: next });
    };

    const handleNotesChange = (e) => {
        updateData('documentations', { document_notes: e.target.value });
    };

    /** The picked/stored files for one slot, each removable on its own. */
    const FileList = ({ field, files }) => (
        <ul className="mt-3 flex flex-col gap-1.5">
            {files.map((file, index) => (
                <li
                    key={`${documentLabel(file)}-${index}`}
                    className="flex items-center justify-between gap-2 pl-2 pr-1 py-1 rounded-md bg-white dark:bg-[#262b33] border border-gray-100 dark:border-gray-700"
                >
                    <span className="text-[11.5px] text-blue-600 dark:text-blue-400 truncate" title={documentLabel(file)}>
                        {documentLabel(file)}
                    </span>
                    <button
                        type="button"
                        onClick={() => removeFileAt(field, index)}
                        className="text-red-400 hover:text-red-600 shrink-0"
                        title="Remove this file"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </li>
            ))}
        </ul>
    );

    // Helper component for file upload rows
    const DocumentRow = ({ title, field }) => {
        const fileInputRef = useRef(null);
        const files = toDocumentList(docs[field]);
        const isUploaded = files.length > 0;
        const isFull = files.length >= MAX_FILES_PER_DOCUMENT;

        return (
            <div className="p-4 border border-gray-100 dark:border-gray-800 rounded-lg bg-gray-50/50 dark:bg-[#31373f] hover:bg-gray-50 dark:hover:bg-[#31373f] transition-colors">
                <input
                    type="file"
                    multiple
                    className="hidden"
                    ref={fileInputRef}
                    onChange={(e) => addFiles(field, e)}
                    accept=".jpg,.jpeg,.png,.pdf"
                />
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 overflow-hidden">
                        {isUploaded ? (
                            <FileCheck className="w-5 h-5 text-green-500 shrink-0" />
                        ) : (
                            <AlertCircle className="w-5 h-5 text-amber-500 shrink-0" />
                        )}
                        <span className="text-[14px] font-medium text-[#334155] dark:text-gray-200 truncate">{title}</span>
                    </div>
                    <div className="flex items-center gap-4 shrink-0">
                        <span className={`text-[12px] font-semibold px-2.5 py-1 rounded-full ${isUploaded ? 'bg-green-100 text-green-700 dark:bg-emerald-900/10 dark:text-emerald-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/10 dark:text-amber-400'}`}>
                            {isUploaded ? `${files.length} selected` : 'Pending'}
                        </span>
                        {/* Always available (until the cap) so more files can be
                            added to a slot that already has one. */}
                        <button
                            type="button"
                            onClick={() => fileInputRef.current.click()}
                            disabled={isFull}
                            title={isFull ? `Maximum ${MAX_FILES_PER_DOCUMENT} files` : 'Add file(s)'}
                            className="text-blue-600 hover:text-blue-800 disabled:text-gray-300 dark:disabled:text-gray-600 disabled:cursor-not-allowed transition-colors"
                        >
                            {isUploaded ? <Plus className="w-5 h-5" /> : <UploadCloud className="w-5 h-5" />}
                        </button>
                    </div>
                </div>
                {isUploaded && <FileList field={field} files={files} />}
            </div>
        );
    };

    /* ── Additional documents: any number, each with its own name ──────────
       Stored as [{ name, files }]. A record saved before this existed comes
       back from the API already folded into one row, so nothing is stranded. */
    const additionalRows = Array.isArray(docs.additional_documents)
        ? docs.additional_documents
        : [];

    const setAdditionalRows = (rows) => {
        updateData('documentations', { additional_documents: rows });
    };

    const addAdditionalRow = () => {
        setAdditionalRows([...additionalRows, { name: '', files: [] }]);
    };

    const updateAdditionalRow = (index, patch) => {
        setAdditionalRows(additionalRows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    };

    const removeAdditionalRow = (index) => {
        setAdditionalRows(additionalRows.filter((_, i) => i !== index));
    };

    /**
     * One extra document: its name and its files, laid out like the fixed rows
     * above so the whole tab reads as one list.
     */
    const AdditionalDocumentRow = ({ row, index }) => {
        const inputRef = useRef(null);
        const files = toDocumentList(row.files);
        const isUploaded = files.length > 0;
        const isFull = files.length >= MAX_FILES_PER_DOCUMENT;

        const addRowFiles = (e) => {
            const picked = Array.from(e.target.files || []);
            if (picked.length === 0) return;
            updateAdditionalRow(index, {
                files: [...files, ...picked].slice(0, MAX_FILES_PER_DOCUMENT),
            });
            e.target.value = '';
        };

        const removeRowFileAt = (fileIndex) => {
            updateAdditionalRow(index, { files: files.filter((_, i) => i !== fileIndex) });
        };

        return (
            <div className="p-4 border border-gray-100 dark:border-gray-800 rounded-lg bg-gray-50/50 dark:bg-[#31373f] transition-colors">
                <input
                    type="file"
                    multiple
                    className="hidden"
                    ref={inputRef}
                    onChange={addRowFiles}
                    accept=".jpg,.jpeg,.png,.pdf"
                />
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                        {isUploaded ? (
                            <FileCheck className="w-5 h-5 text-green-500 shrink-0" />
                        ) : (
                            <AlertCircle className="w-5 h-5 text-amber-500 shrink-0" />
                        )}
                        {/* A filename like "IMG_4821.jpg" tells a reader nothing,
                            so the name belongs on the row itself. */}
                        <input
                            type="text"
                            value={row.name || ''}
                            onChange={(e) => updateAdditionalRow(index, { name: e.target.value })}
                            placeholder="Document name (e.g. Bus pass)"
                            className="flex-1 min-w-0 bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none text-[14px] font-medium text-[#334155] dark:text-gray-200 placeholder:text-gray-400 placeholder:font-normal py-0.5"
                        />
                    </div>
                    <div className="flex items-center gap-4 shrink-0">
                        <span className={`text-[12px] font-semibold px-2.5 py-1 rounded-full ${isUploaded ? 'bg-green-100 text-green-700 dark:bg-emerald-900/10 dark:text-emerald-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/10 dark:text-amber-400'}`}>
                            {isUploaded ? `${files.length} selected` : 'Pending'}
                        </span>
                        <button
                            type="button"
                            onClick={() => inputRef.current.click()}
                            disabled={isFull}
                            title={isFull ? `Maximum ${MAX_FILES_PER_DOCUMENT} files` : 'Add file(s)'}
                            className="text-blue-600 hover:text-blue-800 disabled:text-gray-300 dark:disabled:text-gray-600 disabled:cursor-not-allowed transition-colors"
                        >
                            {isUploaded ? <Plus className="w-5 h-5" /> : <UploadCloud className="w-5 h-5" />}
                        </button>
                        <button
                            type="button"
                            onClick={() => removeAdditionalRow(index)}
                            title="Remove this document"
                            className="text-gray-400 hover:text-red-600 transition-colors"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {isUploaded && (
                    <ul className="mt-3 flex flex-col gap-1.5">
                        {files.map((file, fileIndex) => (
                            <li
                                key={`${documentLabel(file)}-${fileIndex}`}
                                className="flex items-center justify-between gap-2 pl-2 pr-1 py-1 rounded-md bg-white dark:bg-[#262b33] border border-gray-100 dark:border-gray-700"
                            >
                                <span className="text-[11.5px] text-blue-600 dark:text-blue-400 truncate" title={documentLabel(file)}>
                                    {documentLabel(file)}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => removeRowFileAt(fileIndex)}
                                    className="text-red-400 hover:text-red-600 shrink-0"
                                    title="Remove this file"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        );
    };

    return (
        <div>
            <h2 className="text-[22px] font-bold text-[#334155] dark:text-gray-200 mb-8">Documentations</h2>

            <div className="flex flex-col md:flex-row gap-10">
                {/* Left Column - Identification Docs */}
                <div className="flex-1 flex flex-col gap-4">
                    <h3 className="text-[15px] font-semibold text-[#1E293B] dark:text-gray-200 mb-2">Identification & Address</h3>

                    <DocumentRow title="Client Photo ID (Passport/License)" field="photo_id_url" />
                    <DocumentRow title="Proof of Address (Utility Bill)" field="proof_of_address_url" />
                    <DocumentRow title="NHS Medical Exemption Certificate" field="nhs_exemption_certificate_url" />

                    {/* Extra documents, each with its own name. One row per
                        document, styled like the fixed rows above — the old
                        single name field + drop zone could only ever describe
                        one extra document however many files went into it. */}
                    <div className="mt-4">
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-[14px] font-semibold text-[#475569] dark:text-gray-400">Additional Documents</h3>
                            <button
                                type="button"
                                onClick={addAdditionalRow}
                                className="flex items-center gap-1.5 text-[13px] font-semibold text-blue-600 hover:text-blue-800 dark:text-blue-400 transition-colors"
                            >
                                <Plus className="w-4 h-4" /> Add more
                            </button>
                        </div>

                        {additionalRows.length === 0 ? (
                            <button
                                type="button"
                                onClick={addAdditionalRow}
                                className="w-full p-4 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg text-[13px] text-gray-500 dark:text-gray-400 hover:border-blue-400 hover:text-blue-600 transition-colors"
                            >
                                No additional documents — click to add one
                            </button>
                        ) : (
                            <div className="flex flex-col gap-4">
                                {additionalRows.map((row, index) => (
                                    <AdditionalDocumentRow key={index} row={row} index={index} />
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Vertical Dashed Divider */}
                <div className="hidden md:block w-px border-l-2 border-dashed border-gray-200 dark:border-gray-800"></div>

                {/* Right Column - Legal & Care Docs */}
                <div className="flex-1 flex flex-col gap-4">
                    <h3 className="text-[15px] font-semibold text-[#1E293B] dark:text-gray-200 mb-2">Legal & Care Agreements</h3>

                    <DocumentRow title="Signed Admission Contract" field="signed_admission_contract_url" />
                    <DocumentRow title="Pre-Admission Assessment Form" field="pre_admission_assessment_url" />
                    <DocumentRow title="DNAR / Resuscitation Form" field="dnar_form_url" />
                    <DocumentRow title="Power of Attorney Certificate" field="poa_certificate_url" />

                    <div className="mt-4">
                        <FormField
                            label="Document Notes / Location"
                            name="document_notes"
                            type="textarea"
                            rows={4}
                            placeholder="E.g., Original PoA document is kept in the main office safe. Copies provided to family."
                            value={docs.document_notes || ''}
                            onChange={handleNotesChange}
                        />
                    </div>
                </div>

            </div>

        </div>
    );
};

export default Documentations;
